import { CashierBadgeData } from '../../../../shared/user/cashier';
import { captureError } from '../../../utils/errorHandling';
import { NFCTagInterfaceBase } from '../NFCProxy';
import { BaseCashierAction } from '../reducer/reducer';
import { NFCController } from './NFC';

interface SavedInterfaces {
  originalDeviceUID: string;
}

export interface NFCSaveStatus {
  interfaces: SavedInterfaces[];
  current?: string;
}

export interface NFCState {
  manager: NFCController;
  currentNfcTagInterface?: NFCTagInterfaceBase;
  status: string;
  showNfcWarning: boolean;
  badgeForWarning?: CashierBadgeData;
  reprintReason?: string;

  saveState: NFCSaveStatus;
}

interface BaseNFCAction extends BaseCashierAction {
  source: 'nfc';
}

interface NFCProgram extends BaseNFCAction {
  type: 'NFC_PROGRAM';
  badge: CashierBadgeData;
  reason?: string;
}

interface NFCAddInterface extends BaseNFCAction {
  type: 'NFC_ADD_INTERFACE';
  interface: NFCTagInterfaceBase;
}

interface NFCRemoveInterface extends BaseNFCAction {
  type: 'NFC_REMOVE_INTERFACE';
  interface: NFCTagInterfaceBase;
}

interface NFCUpdateStatus extends BaseNFCAction {
  type: 'NFC_UPDATE_STATUS';
  status: string;
}

interface NFCHandleWarning extends BaseNFCAction {
  type: 'NFC_HANDLE_WARNING';
  status: boolean;
}

interface NFCShowWarning extends BaseNFCAction {
  type: 'NFC_SHOW_WARNING';
  badge: CashierBadgeData;
  reason?: string;
}

interface NFCReadBadge extends BaseNFCAction {
  type: 'NFC_READ_BADGE';
  deviceUID: string;
}

interface NFCSetActiveInterface extends BaseNFCAction {
  type: 'NFC_SET_ACTIVE_INTERFACE';
  deviceUID: string;
}

export type NFCAction =
  | NFCProgram
  | NFCUpdateStatus
  | NFCHandleWarning
  | NFCShowWarning
  | NFCAddInterface
  | NFCRemoveInterface
  | NFCReadBadge
  | NFCSetActiveInterface;

export function nfcReducer(state: Readonly<NFCState>, action: NFCAction): NFCState {
  switch (action.type) {
    case 'NFC_PROGRAM': {
      // Handle NFC_PROGRAM action
      if (!state.currentNfcTagInterface) {
        return state;
      }

      void state.manager
        .programNFCBadge(state.currentNfcTagInterface, action.badge, action.reason)
        .then((result): void => {
          if (result) {
            state.manager.printBadge(action.badge, state.reprintReason);
          }
        });

      return state;
    }

    case 'NFC_ADD_INTERFACE': {
      state.manager.addInterface(action.interface);

      return {
        ...state,
        saveState: state.manager.getSaveState(state.currentNfcTagInterface?.originalDeviceUID),
      };
    }

    case 'NFC_REMOVE_INTERFACE': {
      state.manager.removeInterface(action.interface);

      return {
        ...state,
        saveState: state.manager.getSaveState(state.currentNfcTagInterface?.originalDeviceUID),
      };
    }

    case 'NFC_SET_ACTIVE_INTERFACE': {
      // Handle NFC_SET_ACTIVE_INTERFACE action
      const currentInterface = state.manager.setActiveInterface(action.deviceUID);

      if (currentInterface) {
        return {
          ...state,
          currentNfcTagInterface: currentInterface,
        };
      }

      return state;
    }

    case 'NFC_UPDATE_STATUS': {
      // Handle NFC_UPDATE_STATUS action
      return {
        ...state,
        status: action.status,
      };
    }

    case 'NFC_HANDLE_WARNING': {
      if (action.status) {
        if (!state.currentNfcTagInterface || !state.badgeForWarning) {
          return {
            ...state,
            badgeForWarning: undefined,
            showNfcWarning: false,
            reprintReason: undefined,
          };
        }

        void state.manager.handleNfcWarningYes(
          state.currentNfcTagInterface,
          state.badgeForWarning,
          state.reprintReason,
        );
      }

      return {
        ...state,
        badgeForWarning: undefined,
        showNfcWarning: false,
        reprintReason: undefined,
      };
    }

    case 'NFC_SHOW_WARNING': {
      return {
        ...state,
        badgeForWarning: action.badge,
        showNfcWarning: true,
        reprintReason: action.reason,
      };
    }

    case 'NFC_READ_BADGE': {
      const thisInterface = state.manager.getNFCTagInterface(action.deviceUID);

      if (!thisInterface) {
        return state;
      }

      state.manager.readNfcBadge(thisInterface).catch(captureError);
      return state;
    }

    default: {
      return state;
    }
  }
}
